/**
 * Comprehensive validation utilities for AIRFLY
 * Ensures data integrity, security, and UX quality
 */

// ============================================
// EMAIL VALIDATION
// ============================================

/**
 * Validates email format with common typo detection
 */
export function validateEmail(email) {
  const trimmed = String(email || '').trim().toLowerCase();
  
  if (!trimmed) {
    return { valid: false, error: 'Email address is required.' };
  }

  // Basic format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: 'Please enter a valid email address.' };
  }

  // Check for common typos in popular domains
  const commonTypos = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmali.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outlookk.com': 'outlook.com',
  };

  const domain = trimmed.split('@')[1];
  if (commonTypos[domain]) {
    return { 
      valid: false, 
      error: `Did you mean ${trimmed.replace(domain, commonTypos[domain])}?`,
      suggestion: trimmed.replace(domain, commonTypos[domain])
    };
  }

  // Check for suspicious patterns (security)
  if (trimmed.includes('..') || trimmed.startsWith('.') || trimmed.endsWith('.')) {
    return { valid: false, error: 'Email format is invalid.' };
  }

  return { valid: true };
}

// ============================================
// PHONE VALIDATION
// ============================================

/**
 * Validates phone number with country-specific rules
 */
export function validatePhone(phone, countryCode) {
  const phoneDigits = String(phone || '').replace(/\D/g, '');
  const code = String(countryCode || '').trim();

  if (!phoneDigits) {
    return { valid: false, error: 'Phone number is required.' };
  }

  if (!code || !/^\+\d{1,4}$/.test(code)) {
    return { valid: false, error: 'Country code is required (e.g., +61 for Australia).' };
  }

  // Minimum 7 digits (some countries), maximum 15 digits (ITU standard)
  if (phoneDigits.length < 7) {
    return { valid: false, error: 'Phone number is too short.' };
  }

  if (phoneDigits.length > 15) {
    return { valid: false, error: 'Phone number is too long.' };
  }

  // Country-specific validation
  const validations = {
    '+61': { min: 9, max: 10, name: 'Australian' }, // Australia
    '+1': { min: 10, max: 10, name: 'US/Canada' }, // US/Canada
    '+44': { min: 10, max: 10, name: 'UK' }, // UK
    '+91': { min: 10, max: 10, name: 'Indian' }, // India
    '+86': { min: 11, max: 11, name: 'Chinese' }, // China
  };

  const countryValidation = validations[code];
  if (countryValidation) {
    if (phoneDigits.length < countryValidation.min || phoneDigits.length > countryValidation.max) {
      return { 
        valid: false, 
        error: `${countryValidation.name} phone numbers should be ${countryValidation.min}${countryValidation.min !== countryValidation.max ? `-${countryValidation.max}` : ''} digits.` 
      };
    }
  }

  return { valid: true };
}

// ============================================
// PASSENGER VALIDATION
// ============================================

/**
 * Validates passenger mix (infant rules, adult supervision)
 */
export function validatePassengerMix(adults, children, infants) {
  const adultCount = Number(adults) || 0;
  const childCount = Number(children) || 0;
  const infantCount = Number(infants) || 0;
  const total = adultCount + childCount + infantCount;

  if (total === 0) {
    return { valid: false, error: 'At least one passenger is required.' };
  }

  if (total > 9) {
    return { valid: false, error: 'Maximum 9 passengers per booking. For larger groups, please make multiple bookings.' };
  }

  // CRITICAL: Infants require lap seats (1 infant per adult)
  if (infantCount > adultCount) {
    return { 
      valid: false, 
      error: `Each infant must travel with an adult. You have ${infantCount} infant${infantCount > 1 ? 's' : ''} but only ${adultCount} adult${adultCount > 1 ? 's' : ''}.` 
    };
  }

  // Children need adult supervision (at least 1 adult for any children)
  if (childCount > 0 && adultCount === 0) {
    return { valid: false, error: 'Children must travel with at least one adult.' };
  }

  // Infants need adult supervision
  if (infantCount > 0 && adultCount === 0) {
    return { valid: false, error: 'Infants must travel with an adult.' };
  }

  return { valid: true };
}

/**
 * Check for duplicate passengers (same name)
 */
export function validateNoDuplicatePassengers(passengers) {
  const names = passengers.map(p => 
    `${String(p.firstName || '').trim().toLowerCase()} ${String(p.lastName || '').trim().toLowerCase()}`
  ).filter(name => name.trim());

  const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
  
  if (duplicates.length > 0) {
    return { 
      valid: false, 
      error: 'Duplicate passenger names detected. Each passenger must have a unique name.' 
    };
  }

  return { valid: true };
}

// ============================================
// DATE VALIDATION
// ============================================

/**
 * Validates search date range (not too far in future)
 */
export function validateSearchDateRange(departureDate, returnDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const departure = new Date(departureDate);
  const maxAdvanceBookingDays = 330; // ~11 months (airline standard)
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + maxAdvanceBookingDays);

  if (departure > maxDate) {
    return { 
      valid: false, 
      error: `Flights can be booked up to ${Math.floor(maxAdvanceBookingDays / 30)} months in advance. Please select an earlier date.` 
    };
  }

  if (returnDate) {
    const returnDateObj = new Date(returnDate);
    const maxTripLength = 365; // 1 year maximum trip
    const maxReturn = new Date(departure);
    maxReturn.setDate(maxReturn.getDate() + maxTripLength);

    if (returnDateObj > maxReturn) {
      return { 
        valid: false, 
        error: 'Return date cannot be more than 1 year after departure.' 
      };
    }
  }

  return { valid: true };
}

// ============================================
// PRICE VALIDATION
// ============================================

/**
 * Validates price is reasonable (not $0 or negative)
 */
export function validatePrice(price, minPrice = 10, maxPrice = 50000) {
  const amount = Number(price) || 0;

  if (amount <= 0) {
    return { valid: false, error: 'Price must be greater than zero.' };
  }

  if (amount < minPrice) {
    return { 
      valid: false, 
      error: `Price seems too low (minimum $${minPrice}). Please verify the fare.` 
    };
  }

  if (amount > maxPrice) {
    return { 
      valid: false, 
      error: `Price exceeds maximum ($${maxPrice}). For premium bookings, please contact support.` 
    };
  }

  return { valid: true };
}

// ============================================
// PROMO CODE VALIDATION
// ============================================

/**
 * Validates promo code format
 */
export function validatePromoCode(code) {
  const trimmed = String(code || '').trim().toUpperCase();

  if (!trimmed) {
    return { valid: true }; // Empty is OK (promo optional)
  }

  // Promo codes: 4-20 chars, alphanumeric only
  if (trimmed.length < 4 || trimmed.length > 20) {
    return { valid: false, error: 'Promo code should be 4-20 characters.' };
  }

  if (!/^[A-Z0-9]+$/.test(trimmed)) {
    return { valid: false, error: 'Promo code can only contain letters and numbers.' };
  }

  return { valid: true, normalized: trimmed };
}

// ============================================
// XSS PREVENTION
// ============================================

/**
 * Sanitizes user input to prevent XSS
 */
export function sanitizeInput(input) {
  if (!input) return '';
  
  return String(input)
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .slice(0, 500); // Max length for any text input
}

/**
 * Validates name doesn't contain suspicious patterns
 */
export function validateNameSecurity(name, label = 'Name') {
  const trimmed = String(name || '').trim();

  // Check for script injection attempts
  if (/[<>{}]/g.test(trimmed)) {
    return { valid: false, error: `${label} contains invalid characters.` };
  }

  // Check for excessive special characters (potential injection)
  const specialCharCount = (trimmed.match(/[^a-zA-Z\s'-]/g) || []).length;
  if (specialCharCount > 3) {
    return { valid: false, error: `${label} contains too many special characters.` };
  }

  // Check for suspicious patterns (SQL injection attempts)
  if (/drop|delete|insert|update|select|script|alert/i.test(trimmed)) {
    return { valid: false, error: `${label} contains invalid text.` };
  }

  return { valid: true };
}

// ============================================
// AIRPORT CODE VALIDATION
// ============================================

/**
 * Validates airport code format (IATA 3-letter codes)
 */
export function validateAirportCode(code, label = 'Airport') {
  const trimmed = String(code || '').trim().toUpperCase();

  if (!trimmed) {
    return { valid: false, error: `${label} is required.` };
  }

  // Check if it's a city name (valid) or invalid format
  if (trimmed.length < 3) {
    return { valid: false, error: `${label} is too short.` };
  }

  // Airport codes should only contain letters (or be city names with spaces)
  if (!/^[A-Z\s]+$/.test(trimmed)) {
    return { valid: false, error: `${label} should only contain letters.` };
  }

  return { valid: true };
}

/**
 * Validates origin and destination are different
 */
export function validateDifferentAirports(from, to) {
  const fromNormalized = String(from || '').trim().toUpperCase();
  const toNormalized = String(to || '').trim().toUpperCase();

  if (!fromNormalized || !toNormalized) {
    return { valid: true }; // Skip if empty (handled by required validation)
  }

  if (fromNormalized === toNormalized) {
    return { 
      valid: false, 
      error: 'Departure and arrival airports must be different.' 
    };
  }

  return { valid: true };
}
